import {
	CancellableEvent,
	getSubscriptions,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { Create } from '@mui/icons-material';
import { FullPageWrapper } from '@Utils/styles';
import { IconButton } from '@mui/material';
import React from 'react';
import { TFunction } from 'i18next';
import { selectRiskStore } from '@Selectors/Risk.selectors';
import { viewerAccess } from '@innovyze/shared-utils';
import { Category } from '@Types/category.types';
import { Component } from '@Types/component.types';
import { LoFSetup } from '@Components/LoFSetup';
import { CoFSetup } from '@Components/CoFSetup';
import { RiskSetup } from '@Components/RiskSetup';
import { RiskLogs } from '@Components/RiskLogs';
import { useParams } from 'react-router-dom';
import {
	RiskDetailsDatagrid,
	RiskDetailsWithMap,
} from '@Components/RiskDetails';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { TemplateStartBanner } from '@Components/RiskSetup/templates/TemplateStartBanner.component';
import { RiskTabRouteKey, getRiskTabOrder } from '@Utils/riskTabOrder';
import { ResultsTab } from '@Components/RiskDetails/ResultsKpi/ResultsTab.component';

interface FullRiskPageProps {
	getSecondary: () => JSX.Element;
	back: () => void;
	multipleModels: boolean;
	beforeTabChange: (event: CancellableEvent) => CancellableEvent;
	pageIndex: number;
	onAddComponent: (
		forLoF: boolean,
		forInspection: boolean,
		category: Category | undefined,
	) => void;
	onEditComponent: (
		forLoF: boolean,
		forInspection: boolean,
		category: Category | undefined,
		component: Component,
	) => void;
	refreshRiskScore: () => void;
	onEditName: () => void;
	t: TFunction;
}

const FullRiskPage: React.FC<FullRiskPageProps> = ({
	getSecondary,
	back,
	multipleModels,
	beforeTabChange,
	pageIndex,
	onAddComponent,
	onEditComponent,
	refreshRiskScore,
	onEditName,
	t,
}) => {
	const showRiskDetailMap = useIsFeatureEnabled('i-360-risk-results-map');
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();

	const subscriptions = getSubscriptions();
	const hasViewerAccess = viewerAccess(subscriptions);
	const riskStore = selectRiskStore();
	const params: { configId: string } = useParams();

	const isLoaded = riskStore.riskConfig.riskSetting.name.length != 0;

	// The tab that's currently selected shouldn't be dimmed by the loading
	// state — landing on a greyed-out (if still clickable) active tab reads
	// as broken. Viewers are routed away from edit tabs at the landing-
	// decision layer, so this guard only ever needs to cover !isLoaded.
	const activeRouteKey = getRiskTabOrder(isRiskTemplatesEnabled)[pageIndex];

	const riskDetailsTab = showRiskDetailMap ? (
		<RiskDetailsWithMap
			configId={params.configId}
			refreshRiskScore={refreshRiskScore}
		/>
	) : (
		<RiskDetailsDatagrid
			configId={params.configId}
			refreshRiskScore={refreshRiskScore}
		/>
	);

	// Flag on: Results decides from run status whether to show the empty
	// state or the KPI bar above the grid (with or without the map). Flag
	// off: unchanged.
	const resultsContent = isRiskTemplatesEnabled ? (
		<ResultsTab configId={params.configId}>{riskDetailsTab}</ResultsTab>
	) : (
		riskDetailsTab
	);

	const tabsByRouteKey: Record<
		RiskTabRouteKey,
		{ content: React.ReactNode; title: string; disabled?: boolean }
	> = {
		results: {
			content: resultsContent,
			title: t('Results'),
			// Viewers can still see Results (unlike the edit tabs), so this
			// intentionally omits hasViewerAccess — only the loading state
			// dims it, matching the other tabs while riskConfig loads.
			// Gated on the flag: before this PR, Results never had a
			// disabled state at all, and that stays true with the flag off.
			disabled:
				isRiskTemplatesEnabled &&
				!isLoaded &&
				activeRouteKey !== 'results',
		},
		lofSetup: {
			content: (
				<LoFSetup
					onAddComponent={onAddComponent}
					onEditComponent={onEditComponent}
				/>
			),
			title: t('Likelihood of Failure Setup'),
			disabled:
				(!isLoaded && activeRouteKey !== 'lofSetup') || hasViewerAccess,
		},
		cofSetup: {
			content: (
				<CoFSetup
					onAddComponent={onAddComponent}
					onEditComponent={onEditComponent}
				/>
			),
			title: t('Consequence of Failure Setup'),
			disabled:
				(!isLoaded && activeRouteKey !== 'cofSetup') || hasViewerAccess,
		},
		riskSetup: {
			content: <RiskSetup />,
			title: t('Risk Setup'),
			disabled:
				(!isLoaded && activeRouteKey !== 'riskSetup') ||
				hasViewerAccess,
		},
		riskLog: {
			content: <RiskLogs configId={params.configId} />,
			title: t('Log'),
			disabled:
				(!isLoaded && activeRouteKey !== 'riskLog') || hasViewerAccess,
		},
	};

	const tabItems = getRiskTabOrder(isRiskTemplatesEnabled).map(
		routeKey => tabsByRouteKey[routeKey],
	);

	return (
		<>
			{isRiskTemplatesEnabled && (
				<TemplateStartBanner configId={params.configId} />
			)}
			<FullPageWrapper
				title={
					riskStore.riskConfig.riskSetting.name.length == 0
						? ' '
						: riskStore.riskConfig.riskSetting.name
				}
				minorHeader={
					!hasViewerAccess && isLoaded ? (
						<div
							style={{
								paddingLeft: '20px',
								paddingRight: '20px',
							}}>
							<IconButton
								title={t('Edit Name')}
								data-cy="editButton"
								onClick={onEditName}>
								<Create />
							</IconButton>
						</div>
					) : (
						<></>
					)
				}
				applyPadding={true}
				headerUnderline
				secondary={
					riskStore.riskConfig.riskSetting ? getSecondary() : <></>
				}
				back={
					multipleModels
						? {
								action: back,
								label: t('Back to Risk Models'),
						  }
						: undefined
				}
				secondaryNav={{
					items: tabItems,
					itemIndex: pageIndex,
					beforeItemIndexChanged: beforeTabChange,
				}}></FullPageWrapper>
		</>
	);
};

export default FullRiskPage;
